/**
 * Nouveau rendez-vous — dashboard tuteur. `cours`/`etude` redirigent vers
 * l'éditeur de créneaux existant (grille hebdomadaire, cf.
 * _app.tutor.rendez-vous.creneaux.tsx) ; `express`/`entretien` créent un
 * événement CIBLÉ directement (RPC create_direct_rendezvous) pour UN élève
 * choisi, immédiat ou programmé — puis proposent de rejoindre tout de suite
 * ou de partager le lien d'invitation.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Calendar, CalendarClock, GraduationCap, Users, Video, Zap } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import type { RendezVousKind } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { useJoinVisioRoom } from '@/lib/visio/handoff';
import { useTutorStudents } from '@/lib/tutor/queries';
import { useMyVisioSeries } from '@/lib/tutor/visio-series';
import { splitStudentsAndAlternants } from '@/lib/tutor/alternants';
import { translateAlternantError, useSetStudentAlternant } from '@/lib/tutor/student-alternant';
import {
  useAcademicMentees,
  useCreateDirectRendezVous,
  useOffersAcademicMentoring,
  useSetOffersAcademicMentoring,
  type DirectRendezVousKind,
} from '@/lib/tutor/rendezvous';

const TYPE_CARDS: Array<{
  kind: RendezVousKind;
  label: string;
  description: string;
  icon: typeof Zap;
}> = [
  {
    kind: 'express',
    label: 'Appel rapide',
    description: 'Visio ciblée avec un élève que tu suis — maintenant ou programmée.',
    icon: Zap,
  },
  {
    kind: 'entretien',
    label: 'Entretien académique',
    description: 'Gratuit, avec un étudiant alternant qui t’a choisi comme tuteur académique.',
    icon: GraduationCap,
  },
  {
    kind: 'cours',
    label: 'Cours (créneaux payants)',
    description: 'Ouvre des disponibilités hebdomadaires réservables par tes élèves.',
    icon: CalendarClock,
  },
  {
    kind: 'etude',
    label: "Session d'étude (créneaux gratuits)",
    description: 'Comme un cours, mais gratuit — ouvert à tes élèves liés.',
    icon: Calendar,
  },
];

function formatDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function DirectRendezVousForm({
  kind,
  initialStudentId,
}: {
  kind: DirectRendezVousKind;
  initialStudentId?: string;
}) {
  const { user } = useAuth();
  const tutorId = user?.id ?? '';
  const { data: linkedStudents = [] } = useTutorStudents(tutorId);
  const { data: mentees = [] } = useAcademicMentees(tutorId);
  // Séries de points du tuteur (migration 0247) : à choisir dès la création.
  const { data: mySeries = [] } = useMyVisioSeries(tutorId || undefined);
  const { data: offersMentoring, isLoading: mentoringLoading } =
    useOffersAcademicMentoring(tutorId);
  const setOffersMentoring = useSetOffersAcademicMentoring(tutorId);
  const create = useCreateDirectRendezVous();
  const attachAlternant = useSetStudentAlternant(tutorId);
  const joinVisioRoom = useJoinVisioRoom();

  const [studentId, setStudentId] = useState(initialStudentId ?? '');
  const [mode, setMode] = useState<'now' | 'later'>('now');
  const [scheduledAt, setScheduledAt] = useState(() =>
    formatDateTimeLocal(new Date(Date.now() + 60 * 60_000)),
  );
  const [duration, setDuration] = useState(30);
  const [seriesId, setSeriesId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    reservationId: string;
    shareUrl: string;
    seriesTitle: string | null;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  // Entretien académique : les étudiants alternants rattachés à ce tuteur, PLUS ceux
  // qui lui sont reliés sans l'avoir encore choisi (« à rattacher »). Sans eux la liste
  // restait vide (impasse) : un entretien exige d'être leur tuteur académique côté
  // serveur, on les rattache donc à la création du rendez-vous.
  const candidates = useMemo(() => {
    if (kind === 'express') {
      return linkedStudents
        .filter((s) => s.status === 'active')
        .map((s) => ({ id: s.student_id, name: s.display_name, needsAttach: false }));
    }
    return splitStudentsAndAlternants(linkedStudents, mentees).alternants.map((a) => ({
      id: a.studentId,
      name: a.attached ? a.displayName : `${a.displayName} — à rattacher`,
      needsAttach: !a.attached,
    }));
  }, [kind, linkedStudents, mentees]);
  const selected = candidates.find((c) => c.id === studentId);

  const handleCreate = async () => {
    setError(null);
    if (!studentId) {
      setError('Choisis un élève.');
      return;
    }
    const startsAt = mode === 'now' ? new Date() : new Date(scheduledAt);
    if (mode === 'later' && Number.isNaN(startsAt.getTime())) {
      setError('Date invalide.');
      return;
    }
    try {
      if (selected?.needsAttach) {
        // Rattachement AVANT la création : sans lui, create_direct_rendezvous refuse
        // (not_academic_student). Si le rattachement échoue, aucun rendez-vous n'est créé.
        try {
          await attachAlternant.mutateAsync({ studentId, alternant: true });
        } catch (attachErr) {
          setError(translateAlternantError(attachErr instanceof Error ? attachErr.message : ''));
          return;
        }
      }
      const res = await create.mutateAsync({
        kind,
        studentId,
        startsAt,
        durationMinutes: duration,
        seriesId: seriesId || null,
      });
      setResult({
        reservationId: res.reservationId,
        shareUrl: `${window.location.origin}/rendez-vous/${res.reservationId}`,
        seriesTitle: mySeries.find((s) => s.id === seriesId)?.title ?? null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Création impossible.');
    }
  };

  const handleShare = async () => {
    if (!result) return;
    try {
      if (navigator.share) {
        await navigator.share({ url: result.shareUrl, title: 'Rejoins la visio' });
        return;
      }
    } catch {
      // annulé/non supporté — repli copie presse-papier
    }
    await navigator.clipboard.writeText(result.shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (kind === 'entretien' && !mentoringLoading && !offersMentoring) {
    return (
      <div className="border-ink-100 rounded-2xl border bg-white p-5 text-center">
        <GraduationCap className="text-ink-300 mx-auto h-8 w-8" />
        <p className="text-ink-700 mt-2 font-sans text-sm font-semibold">
          Mentorat académique désactivé
        </p>
        <p className="text-ink-500 mt-1 font-sans text-xs">
          Active-le pour que des étudiants alternants puissent te choisir comme tuteur académique et
          réserver des entretiens gratuits.
        </p>
        <Button
          className="mt-4 w-full"
          onClick={() => setOffersMentoring.mutate(true)}
          disabled={setOffersMentoring.isPending}
        >
          Activer le mentorat académique
        </Button>
      </div>
    );
  }

  if (result) {
    return (
      <div className="border-ink-100 space-y-4 rounded-2xl border bg-white p-5 text-center">
        <p className="font-display text-ink-900 text-base font-bold">Rendez-vous créé</p>
        {result.seriesTitle && (
          <p className="text-ink-500 text-xs">
            Points à aborder : <span className="font-semibold">{result.seriesTitle}</span>
          </p>
        )}
        <div className="flex flex-col gap-2">
          {mode === 'now' && (
            <Button
              onClick={() => joinVisioRoom.mutate(result.reservationId)}
              loading={joinVisioRoom.isPending}
              className="min-h-[2.75rem] w-full active:scale-[0.98]"
            >
              <Video className="size-4" />
              Rejoindre maintenant
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleShare}
            className="min-h-[2.75rem] w-full active:scale-[0.98]"
          >
            {copied ? 'Lien copié !' : "Partager le lien d'invitation"}
          </Button>
        </div>
        <Link
          to="/tutor/rendez-vous"
          className="text-primary-600 inline-block text-sm font-semibold"
        >
          Retour aux rendez-vous
        </Link>
      </div>
    );
  }

  return (
    <div className="border-ink-100 space-y-4 rounded-2xl border bg-white p-5">
      <div>
        <label
          htmlFor="rdv-student"
          className="font-display text-ink-700 mb-1.5 block text-xs font-semibold"
        >
          {kind === 'express' ? 'Élève' : 'Étudiant alternant'}
        </label>
        <select
          id="rdv-student"
          value={studentId}
          onChange={(e) => setStudentId(e.target.value)}
          className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none"
        >
          <option value="">— Choisir —</option>
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {candidates.length === 0 && (
          <p className="text-ink-500 mt-1.5 font-sans text-xs">
            {kind === 'express'
              ? "Aucun élève lié actif pour l'instant."
              : "Aucun étudiant alternant relié à toi pour l'instant. Invite-en un depuis Mes contacts, ou passe un de tes élèves en étudiant alternant."}
          </p>
        )}
        {selected?.needsAttach && (
          <p className="text-ink-500 mt-1.5 font-sans text-xs">
            Il/elle sera rattaché·e à toi comme tuteur académique en créant ce rendez-vous.
          </p>
        )}
      </div>

      <div className="flex gap-2">
        {(['now', 'later'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={cn(
              'min-h-[2.75rem] flex-1 rounded-xl border px-3 text-sm font-semibold transition-colors active:scale-[0.98]',
              mode === m
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-ink-200 text-ink-600 bg-white',
            )}
          >
            {m === 'now' ? 'Maintenant' : 'Programmer'}
          </button>
        ))}
      </div>

      {mode === 'later' && (
        <div>
          <label
            htmlFor="rdv-when"
            className="font-display text-ink-700 mb-1.5 block text-xs font-semibold"
          >
            Date et heure
          </label>
          <input
            id="rdv-when"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none"
          />
        </div>
      )}

      <div>
        <label
          htmlFor="rdv-duration"
          className="font-display text-ink-700 mb-1.5 block text-xs font-semibold"
        >
          Durée
        </label>
        <select
          id="rdv-duration"
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
          className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none"
        >
          {[15, 30, 45, 60].map((m) => (
            <option key={m} value={m}>
              {m} min
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor="rdv-series"
          className="font-display text-ink-700 mb-1.5 block text-xs font-semibold"
        >
          Série de points à aborder (optionnel)
        </label>
        {mySeries.length > 0 ? (
          <>
            <select
              id="rdv-series"
              value={seriesId}
              onChange={(e) => setSeriesId(e.target.value)}
              className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none"
            >
              <option value="">Aucune</option>
              {mySeries.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
            <p className="text-ink-500 mt-1.5 font-sans text-xs">
              Proposée pendant la visio (points à cocher) et pour le résumé du rendez-vous — tu
              pourras la changer.
            </p>
          </>
        ) : (
          <p className="text-ink-500 font-sans text-xs">
            Tu n’as pas encore de série.{' '}
            <Link to="/tutor/rendez-vous/series" className="text-primary-600 font-semibold">
              Crées-en dans Rendez-vous → Séries
            </Link>{' '}
            pour préparer les points à aborder.
          </p>
        )}
      </div>

      {error && <p className="text-accent-600 font-sans text-xs">{error}</p>}

      <Button
        onClick={handleCreate}
        disabled={create.isPending || attachAlternant.isPending || !studentId}
        className="w-full"
      >
        {create.isPending ? 'Création…' : 'Créer le rendez-vous'}
      </Button>
    </div>
  );
}

/** 'express'/'entretien' uniquement — 'cours'/'etude' redirigent vers l'éditeur de créneaux, jamais préremplis via l'URL. */
export function initialKindFromSearch(raw: string | undefined): DirectRendezVousKind | null {
  return raw === 'express' || raw === 'entretien' ? raw : null;
}

export function TutorNewRendezVousPage() {
  const navigate = useNavigate();
  // Préremplissage depuis la fiche élève ("Nouveau rendez-vous") ou la carte
  // d'un étudiant alternant (section "Mes étudiants" de /tutor/contacts).
  const search = useSearch({ from: '/app/tutor/rendez-vous/new' });
  const [kind, setKind] = useState<RendezVousKind | null>(() => initialKindFromSearch(search.kind));

  return (
    <div className="mx-auto max-w-lg px-4 py-6 sm:py-8">
      <button
        type="button"
        onClick={() => (kind ? setKind(null) : navigate({ to: '/tutor/rendez-vous' }))}
        aria-label="Retour"
        className="border-ink-200 text-ink-700 hover:bg-ink-50 mb-3 flex h-11 w-11 items-center justify-center rounded-full border bg-white shadow-sm transition-colors active:scale-95"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
        Nouveau rendez-vous
      </h1>

      {!kind ? (
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {TYPE_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.kind}
                type="button"
                onClick={() => {
                  if (card.kind === 'cours' || card.kind === 'etude') {
                    void navigate({ to: '/tutor/rendez-vous/creneaux' });
                    return;
                  }
                  setKind(card.kind);
                }}
                className="border-ink-100 hover:border-primary-300 flex flex-col items-start gap-2 rounded-2xl border bg-white p-4 text-left transition-all active:scale-[0.98]"
              >
                <div className="bg-primary-50 flex h-10 w-10 items-center justify-center rounded-full">
                  <Icon className="text-primary-600 h-5 w-5" />
                </div>
                <p className="text-ink-900 text-sm font-semibold">{card.label}</p>
                <p className="text-ink-500 text-xs">{card.description}</p>
              </button>
            );
          })}
          {/* Réunion libre (Phase D) : distincte des 4 cartes ci-dessus (pas de RendezVousKind,
              pas d'élève à choisir) — bouton pleine largeur pour la démarquer visuellement. */}
          <button
            type="button"
            onClick={() => void navigate({ to: '/tutor/rendez-vous/reunion' })}
            className="border-ink-100 hover:border-primary-300 col-span-full flex items-center gap-3 rounded-2xl border bg-white p-4 text-left transition-all active:scale-[0.98]"
          >
            <div className="bg-primary-50 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
              <Users className="text-primary-600 h-5 w-5" />
            </div>
            <div>
              <p className="text-ink-900 text-sm font-semibold">Réunion libre (à plusieurs)</p>
              <p className="text-ink-500 text-xs">
                Aucun élève à choisir — partage un lien, invités possibles.
              </p>
            </div>
          </button>
        </div>
      ) : (
        <div className="mt-5">
          <DirectRendezVousForm
            kind={kind as DirectRendezVousKind}
            initialStudentId={search.studentId}
          />
        </div>
      )}
    </div>
  );
}
